/**
 * Ollama provider adapter — local LLM inference.
 * Connects to Ollama at configurable endpoint (default localhost:11434).
 */

import type {
  AIProviderAdapter,
  AICompletionRequest,
  AICompletionResponse,
} from './base-provider';

const DEFAULT_ENDPOINT = 'http://localhost:11434';
const DISCOVERY_TIMEOUT_MS = 5_000;
const COMPLETION_TIMEOUT_MS = 30_000;

/** Models that are embedding-only and should be filtered out. */
const EMBEDDING_MODEL_PATTERNS = [/embed/, /bge/, /nomic-embed/];

function isEmbeddingModel(name: string): boolean {
  const lower = name.toLowerCase();
  return EMBEDDING_MODEL_PATTERNS.some(p => p.test(lower));
}

function getEndpoint(): string {
  try {
    return import.meta.env.VITE_OLLAMA_ENDPOINT || DEFAULT_ENDPOINT;
  } catch {
    return DEFAULT_ENDPOINT;
  }
}

export class OllamaProvider implements AIProviderAdapter {
  readonly id = 'ollama';
  readonly name = 'Ollama (Local)';
  readonly supportsStructuredOutput = false;
  readonly supportsGeneration = true;

  private cachedModels: string[] | null = null;
  private modelsCachedAt = 0;
  private static MODEL_CACHE_TTL = 60_000;

  private async getModels(): Promise<string[]> {
    const now = Date.now();
    if (this.cachedModels && now - this.modelsCachedAt < OllamaProvider.MODEL_CACHE_TTL) {
      return this.cachedModels;
    }
    const models = await this.listModels();
    this.cachedModels = models;
    this.modelsCachedAt = now;
    return models;
  }

  async isAvailable(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS);
      const res = await fetch(`${getEndpoint()}/api/tags`, {
        signal: controller.signal,
      });
      clearTimeout(timer);
      return res.ok;
    } catch {
      return false;
    }
  }

  async listModels(): Promise<string[]> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS);

    try {
      const res = await fetch(`${getEndpoint()}/api/tags`, {
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (!res.ok) return [];

      const data = (await res.json()) as { models?: { name: string }[] };
      if (!data.models) return [];

      return data.models
        .map(m => m.name)
        .filter(name => !isEmbeddingModel(name));
    } catch {
      clearTimeout(timer);
      return [];
    }
  }

  async complete(request: AICompletionRequest): Promise<AICompletionResponse> {
    const models = await this.getModels();
    const model = request.model ?? models[0];
    if (!model) {
      throw new Error('Ollama: no models available');
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), COMPLETION_TIMEOUT_MS);
    const start = performance.now();

    try {
      const res = await fetch(`${getEndpoint()}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          prompt: request.prompt,
          system: request.systemPrompt,
          stream: false,
          options: {
            num_predict: request.maxTokens ?? 512,
            temperature: request.temperature ?? 0.3,
          },
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`Ollama returned ${res.status}: ${errText}`);
      }

      const data = (await res.json()) as {
        response: string;
        eval_count?: number;
        prompt_eval_count?: number;
      };

      return {
        text: data.response.trim(),
        model,
        provider: this.id,
        tokensUsed: (data.eval_count ?? 0) + (data.prompt_eval_count ?? 0),
        latencyMs: Math.round(performance.now() - start),
      };
    } catch (err) {
      clearTimeout(timer);
      throw err;
    }
  }

  async validate(): Promise<{ valid: boolean; error?: string }> {
    const available = await this.isAvailable();
    if (!available) {
      return { valid: false, error: 'Ollama is not reachable' };
    }

    const models = await this.listModels();
    if (models.length === 0) {
      return { valid: false, error: 'Ollama is running but no chat models found' };
    }

    return { valid: true };
  }
}
